
import React from 'react';
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Heart, AlertTriangle, Clock, XCircle } from "lucide-react";

interface CrawlJobHealthIndicatorProps {
  healthStatus: 'healthy' | 'warning' | 'stale' | 'no_heartbeat';
  secondsSinceHeartbeat?: number;
  lastHeartbeat?: string;
  retryCount?: number;
  maxRetries?: number;
}

export const CrawlJobHealthIndicator: React.FC<CrawlJobHealthIndicatorProps> = ({
  healthStatus,
  secondsSinceHeartbeat,
  lastHeartbeat,
  retryCount = 0,
  maxRetries = 3
}) => {
  const getHealthBadge = () => {
    switch (healthStatus) {
      case 'healthy':
        return (
          <Badge variant="default" className="bg-green-500 flex items-center gap-1">
            <Heart className="h-3 w-3" />
            Healthy
          </Badge>
        );
      case 'warning':
        return (
          <Badge variant="default" className="bg-yellow-500 flex items-center gap-1">
            <AlertTriangle className="h-3 w-3" />
            Warning
          </Badge>
        );
      case 'stale':
        return (
          <Badge variant="default" className="bg-orange-500 flex items-center gap-1">
            <Clock className="h-3 w-3" />
            Stale
          </Badge>
        );
      case 'no_heartbeat':
        return (
          <Badge variant="destructive" className="flex items-center gap-1">
            <XCircle className="h-3 w-3" />
            No Heartbeat
          </Badge>
        );
      default:
        return null;
    }
  };

  const getTooltipContent = () => {
    const parts = [];
    
    if (lastHeartbeat) {
      parts.push(`Last heartbeat: ${new Date(lastHeartbeat).toLocaleString()}`);
    }
    
    if (secondsSinceHeartbeat !== undefined) {
      const minutes = Math.floor(secondsSinceHeartbeat / 60);
      const seconds = secondsSinceHeartbeat % 60;
      parts.push(`Time since heartbeat: ${minutes}m ${seconds}s`);
    }
    
    if (retryCount > 0) {
      parts.push(`Retries: ${retryCount}/${maxRetries}`);
    }
    
    return parts.join('\n');
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="cursor-help">
            {getHealthBadge()}
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <div className="whitespace-pre-line text-sm">
            {getTooltipContent()}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
